// Bind each legal entity to a class in the accounting system.
//
// Fern's payroll splits across four classes — Admin, Finance, Brand and
// Strategy, Operations — and they model those as legal entities. A payroll run
// belongs to exactly one entity, so one run posts one journal carrying one
// class, and nothing has to be subtotalled by department.
//
// The important behaviour is what happens when nothing is bound: the journal
// posts with no dimension at all, exactly as it did before. Every tenant that
// does not use classes should be able to ignore this section completely, so it
// hides itself entirely unless the ledger actually has the dimension.
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Loader2, Tags } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { getApiErrorMessage } from '@/lib/apiError';
import { payrollApi } from '../api/payrollApi';
import type { LegalEntityLite } from '../types';
import {
  getDimensionMappings, getDimensionValues, setDimensionMappings,
  type AccountingProvider, type DimensionMapping, type DimensionValue,
} from '@/services/accountingService';

interface Props {
  provider?: AccountingProvider;
  /** accounting:map — read-only without it. */
  canMap: boolean;
  systemName: string;
  /** The company whose ledger is being configured. Class bindings live inside
   *  ONE company's configuration now, so this component reads and writes that
   *  company's — it no longer binds every entity at once, which would have
   *  written one company's classes into another's ledger. */
  legalEntityId: string;
}

export const EntityClassMapping: React.FC<Props> = ({ provider = 'quickbooks', canMap, systemName, legalEntityId }) => {
  const { t } = useTranslation();
  const [entities, setEntities] = useState<LegalEntityLite[]>([]);
  const [values, setValues] = useState<DimensionValue[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [supported, setSupported] = useState(true);

  const load = useCallback(async () => {
    if (!legalEntityId) return;
    setLoading(true);
    try {
      const [ents, vals, maps] = await Promise.all([
        payrollApi.listLegalEntities(),
        getDimensionValues(provider, legalEntityId, 'class'),
        getDimensionMappings(provider, legalEntityId, 'class'),
      ]);
      setEntities(ents);
      setValues(vals);
      const next: Record<string, string> = {};
      (maps as DimensionMapping[]).forEach((m) => { next[m.source_id] = m.external_value_id; });
      setDraft(next);
    } catch {
      // A ledger with no class dimension answers 422, and an unreachable one
      // fails some other way. Either way there is nothing useful to show and
      // this is a section of a settings screen, not the screen itself — so it
      // hides rather than pushing an error at someone who came to map
      // accounts.
      setSupported(false);
    } finally {
      setLoading(false);
    }
  }, [provider, legalEntityId]);

  useEffect(() => { void load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      // Only THIS company's binding. Writing every entity's class into one
      // company's ledger configuration is what the entity boundary exists to
      // prevent.
      const payload: DimensionMapping[] = [{
        source_kind: 'legal_entity',
        source_id: legalEntityId,
        external_value_id: draft[legalEntityId] || '',
      }];
      await setDimensionMappings(provider, legalEntityId, 'class', payload);
      toast.success(t('accounting.classesSaved', {
        defaultValue: 'Saved. New payroll journals will carry these classes.',
      }));
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('accounting.classesSaveFailed', {
        defaultValue: 'Could not save the class mapping.',
      })));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !supported || entities.length === 0) return null;

  return (
    <Card>
      <CardContent className="space-y-4 py-5">
        <div className="flex items-start gap-3">
          <Tags className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
          <div className="space-y-1">
            <h3 className="text-sm font-medium text-foreground">
              {t('accounting.classesTitle', { defaultValue: 'Classes' })}
            </h3>
            <p className="text-xs text-muted-foreground">
              {t('accounting.classesHelp', {
                defaultValue:
                  'A payroll run belongs to one legal entity, so its journal can carry one {{system}} class. Leave an entity blank and its journals post without a class, exactly as they do today.',
                system: systemName,
              })}
            </p>
          </div>
        </div>

        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-left text-xs">
            <thead className="bg-muted/50 text-muted-foreground">
              <tr>
                <th scope="col" className="px-3 py-2 font-medium">
                  {t('accounting.entity', { defaultValue: 'Legal entity' })}
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  {t('accounting.classIn', { defaultValue: 'Class in {{system}}', system: systemName })}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {entities.map((e) => (
                <tr key={e.id}>
                  <td className="px-3 py-2 text-foreground">{e.name}</td>
                  <td className="px-3 py-2">
                    {canMap ? (
                      <select
                        aria-label={t('accounting.classFor', {
                          defaultValue: '{{system}} class for {{name}}', system: systemName, name: e.name,
                        })}
                        className="h-8 w-full min-w-[14rem] rounded-md border border-border bg-background px-2 text-xs text-foreground"
                        value={draft[e.id] || ''}
                        disabled={saving}
                        onChange={(ev) => setDraft((d) => ({ ...d, [e.id]: ev.target.value }))}
                      >
                        <option value="">{t('accounting.noClass', { defaultValue: '— no class —' })}</option>
                        {values.map((v) => (
                          <option key={v.id} value={v.id} disabled={!v.active}>
                            {v.full_name || v.name}
                            {v.active ? '' : ` · ${t('accounting.inactive', { defaultValue: 'inactive' })}`}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-foreground">
                        {values.find((v) => v.id === draft[e.id])?.name || '—'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {canMap && (
          <div className="flex justify-end">
            <Button size="sm" disabled={saving} onClick={() => void save()}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
              {t('accounting.saveClasses', { defaultValue: 'Save classes' })}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EntityClassMapping;
